/**
 * PerformanceMonitor - Tracks freeze/thaw performance metrics for WebGL management
 * Exposes metrics to devtools for debugging and optimization
 */
class PerformanceMonitor {
    static instance;
    freezeThawMetrics = {
        freezeCount: 0,
        thawCount: 0,
        totalFreezeLatencyMs: 0,
        totalThawLatencyMs: 0,
        totalSnapshotCaptureMs: 0,
        totalSnapshotRestoreMs: 0,
        totalMemoryReclaimedBytes: 0,
        lastFreezeTimestamp: null,
        lastThawTimestamp: null,
        averageFreezeLatencyMs: 0,
        averageThawLatencyMs: 0,
        averageSnapshotCaptureMs: 0,
        averageSnapshotRestoreMs: 0,
    };
    frameTimeMetrics = {
        frameCount: 0,
        totalFrameTimeMs: 0,
        averageFrameTimeMs: 0,
        maxFrameTimeMs: 0,
        minFrameTimeMs: Infinity,
        droppedFrames: 0,
    };
    frameTimeHistory = [];
    maxHistorySize = 1000;
    rafId = null;
    lastFrameTime = 0;
    isMonitoring = false;
    constructor() { }
    static getInstance() {
        if (!PerformanceMonitor.instance) {
            PerformanceMonitor.instance = new PerformanceMonitor();
        }
        return PerformanceMonitor.instance;
    }
    // ============================================
    // Freeze/Thaw Metrics
    // ============================================
    recordFreezeStart() {
        return performance.now();
    }
    recordFreezeEnd(startTime, memoryReclaimed = 0, snapshotCaptureMs = 0) {
        const latency = performance.now() - startTime;
        this.freezeThawMetrics.freezeCount++;
        this.freezeThawMetrics.totalFreezeLatencyMs += latency;
        this.freezeThawMetrics.totalSnapshotCaptureMs += snapshotCaptureMs;
        this.freezeThawMetrics.totalMemoryReclaimedBytes += memoryReclaimed;
        this.freezeThawMetrics.lastFreezeTimestamp = Date.now();
        this.freezeThawMetrics.averageFreezeLatencyMs =
            this.freezeThawMetrics.totalFreezeLatencyMs / this.freezeThawMetrics.freezeCount;
        this.freezeThawMetrics.averageSnapshotCaptureMs =
            this.freezeThawMetrics.totalSnapshotCaptureMs / this.freezeThawMetrics.freezeCount;
    }
    recordThawStart() {
        return performance.now();
    }
    recordThawEnd(startTime, snapshotRestoreMs = 0) {
        const latency = performance.now() - startTime;
        this.freezeThawMetrics.thawCount++;
        this.freezeThawMetrics.totalThawLatencyMs += latency;
        this.freezeThawMetrics.totalSnapshotRestoreMs += snapshotRestoreMs;
        this.freezeThawMetrics.lastThawTimestamp = Date.now();
        this.freezeThawMetrics.averageThawLatencyMs =
            this.freezeThawMetrics.totalThawLatencyMs / this.freezeThawMetrics.thawCount;
        this.freezeThawMetrics.averageSnapshotRestoreMs =
            this.freezeThawMetrics.totalSnapshotRestoreMs / this.freezeThawMetrics.thawCount;
    }
    // ============================================
    // Frame Time Monitoring
    // ============================================
    startFrameMonitoring() {
        if (this.isMonitoring)
            return;
        this.isMonitoring = true;
        this.lastFrameTime = performance.now();
        this.tick();
    }
    stopFrameMonitoring() {
        this.isMonitoring = false;
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }
    tick() {
        if (!this.isMonitoring)
            return;
        const now = performance.now();
        const frameTime = now - this.lastFrameTime;
        this.lastFrameTime = now;
        this.frameTimeMetrics.frameCount++;
        this.frameTimeMetrics.totalFrameTimeMs += frameTime;
        this.frameTimeMetrics.averageFrameTimeMs =
            this.frameTimeMetrics.totalFrameTimeMs / this.frameTimeMetrics.frameCount;
        this.frameTimeMetrics.maxFrameTimeMs = Math.max(this.frameTimeMetrics.maxFrameTimeMs, frameTime);
        this.frameTimeMetrics.minFrameTimeMs = Math.min(this.frameTimeMetrics.minFrameTimeMs, frameTime);
        // Count dropped frames (>16.67ms for 60fps)
        if (frameTime > 16.67) {
            this.frameTimeMetrics.droppedFrames++;
        }
        // Keep history for percentile calculations
        this.frameTimeHistory.push(frameTime);
        if (this.frameTimeHistory.length > this.maxHistorySize) {
            this.frameTimeHistory.shift();
        }
        this.rafId = requestAnimationFrame(() => this.tick());
    }
    // ============================================
    // Reporting
    // ============================================
    getReport() {
        const memory = typeof window !== 'undefined' && window.performance && window.performance.memory // ARC-1-keep: legacy bridge
            ? window.performance.memory // ARC-1-keep: legacy bridge
            : { usedJSHeapSize: 0, totalJSHeapSize: 0, jsHeapSizeLimit: 0 };
        return {
            freezeThaw: { ...this.freezeThawMetrics },
            frameTime: { ...this.frameTimeMetrics },
            memory: {
                currentHeapUsed: memory.usedJSHeapSize || 0,
                currentHeapTotal: memory.totalJSHeapSize || 0,
                peakHeapUsed: memory.usedJSHeapSize || 0,
                webglContextsActive: 0, // Would need to be tracked externally
            },
            timestamp: Date.now(),
        };
    }
    logReport() {
        const report = this.getReport();
        console.group('[PerformanceMonitor] WebGL Freeze/Thaw Report');
        console.table({
            'Freeze Count': report.freezeThaw.freezeCount,
            'Thaw Count': report.freezeThaw.thawCount,
            'Avg Freeze Latency (ms)': report.freezeThaw.averageFreezeLatencyMs.toFixed(2),
            'Avg Thaw Latency (ms)': report.freezeThaw.averageThawLatencyMs.toFixed(2),
            'Avg Snapshot Capture (ms)': report.freezeThaw.averageSnapshotCaptureMs.toFixed(2),
            'Avg Snapshot Restore (ms)': report.freezeThaw.averageSnapshotRestoreMs.toFixed(2),
            'Total Memory Reclaimed (MB)': (report.freezeThaw.totalMemoryReclaimedBytes / 1024 / 1024).toFixed(2),
            'Avg Frame Time (ms)': report.frameTime.averageFrameTimeMs.toFixed(2),
            'Max Frame Time (ms)': report.frameTime.maxFrameTimeMs.toFixed(2),
            'Dropped Frames': report.frameTime.droppedFrames,
            'Current Heap (MB)': (report.memory.currentHeapUsed / 1024 / 1024).toFixed(2),
        });
        console.groupEnd();
    }
    // ============================================
    // DevTools Integration
    // ============================================
    exposeToDevTools() {
        if (typeof window === 'undefined')
            return;
        // Expose to window for console access
        window.__VNINVESTCHARTS_PERF__ = {
            getReport: () => this.getReport(),
            logReport: () => this.logReport(),
            startFrameMonitoring: () => this.startFrameMonitoring(),
            stopFrameMonitoring: () => this.stopFrameMonitoring(),
            reset: () => this.reset(),
        };
    }
    // ============================================
    // Reset
    // ============================================
    reset() {
        this.freezeThawMetrics = {
            freezeCount: 0,
            thawCount: 0,
            totalFreezeLatencyMs: 0,
            totalThawLatencyMs: 0,
            totalSnapshotCaptureMs: 0,
            totalSnapshotRestoreMs: 0,
            totalMemoryReclaimedBytes: 0,
            lastFreezeTimestamp: null,
            lastThawTimestamp: null,
            averageFreezeLatencyMs: 0,
            averageThawLatencyMs: 0,
            averageSnapshotCaptureMs: 0,
            averageSnapshotRestoreMs: 0,
        };
        this.frameTimeMetrics = {
            frameCount: 0,
            totalFrameTimeMs: 0,
            averageFrameTimeMs: 0,
            maxFrameTimeMs: 0,
            minFrameTimeMs: Infinity,
            droppedFrames: 0,
        };
        this.frameTimeHistory = [];
    }
    // ============================================
    // Percentile Calculations
    // ============================================
    getFrameTimePercentile(percentile) {
        if (this.frameTimeHistory.length === 0)
            return 0;
        const sorted = [...this.frameTimeHistory].sort((a, b) => a - b);
        const index = Math.ceil(percentile / 100 * sorted.length) - 1;
        return sorted[Math.max(0, index)];
    }
    getFrameTimeStats() {
        return {
            p50: this.getFrameTimePercentile(50),
            p90: this.getFrameTimePercentile(90),
            p95: this.getFrameTimePercentile(95),
            p99: this.getFrameTimePercentile(99),
        };
    }
}
export const performanceMonitor = PerformanceMonitor.getInstance();
export default PerformanceMonitor;
